import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useI3SiteContent } from '../cms/useI3SiteContent';
import '../styles/I3Modal.css';
import '../styles/I3SaveTheDate.css';

const storageKey = (id: string) => `i3_save_the_date_dismissed_${id}`;

type Props = {
  /** Show only after welcome intro has finished */
  introDone: boolean;
};

/**
 * Post-intro Save the Date announcement. Shown once per announcement `id`
 * until the visitor dismisses (localStorage).
 */
const I3SaveTheDateModal: React.FC<Props> = ({ introDone }) => {
  const { content } = useI3SiteContent();
  const announcement = content.saveTheDate;
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!introDone || !announcement?.enabled) {
      setOpen(false);
      return;
    }
    try {
      if (localStorage.getItem(storageKey(announcement.id))) {
        setOpen(false);
        return;
      }
    } catch {
      /* private mode — still show */
    }
    setOpen(true);
  }, [introDone, announcement]);

  const dismiss = useCallback(() => {
    if (announcement?.id) {
      try {
        localStorage.setItem(storageKey(announcement.id), '1');
      } catch {
        /* ignore */
      }
    }
    setOpen(false);
  }, [announcement?.id]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dismiss();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, dismiss]);

  if (!open || !announcement) return null;

  const primaryTo = announcement.primaryCtaTo?.trim() || '/events';
  const primaryLabel = announcement.primaryCtaLabel?.trim() || 'View events';
  const dismissLabel = announcement.dismissLabel?.trim() || 'Got it';

  return (
    <div
      className="i3-modal-overlay i3-std-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="i3-save-the-date-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) dismiss();
      }}
    >
      <div className="i3-modal i3-std-modal">
        <button
          type="button"
          className="i3-modal-close i3-std-close"
          onClick={dismiss}
          aria-label="Close"
        >
          ×
        </button>
        <div className="i3-std-body">
          {announcement.eyebrow ? (
            <p className="i3-std-eyebrow">{announcement.eyebrow}</p>
          ) : null}
          <h2 id="i3-save-the-date-title" className="i3-std-title">
            {announcement.title}
          </h2>
          {announcement.subtitle ? (
            <p className="i3-std-subtitle">{announcement.subtitle}</p>
          ) : null}
          <div className="i3-std-when">
            <p className="i3-std-date">{announcement.date}</p>
            {announcement.time ? <p className="i3-std-time">{announcement.time}</p> : null}
          </div>
          {announcement.body ? <p className="i3-std-body-copy">{announcement.body}</p> : null}
          <div className="i3-std-actions">
            <Link to={primaryTo} className="i3-cta i3-cta-primary" onClick={dismiss}>
              {primaryLabel}
            </Link>
            <button type="button" className="i3-cta i3-cta-secondary" onClick={dismiss}>
              {dismissLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default I3SaveTheDateModal;
